import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, ButtonLink, Content, FormMessage, Header, Icon, Spinner, Text } from '../common/components';
import routes from '../common/constants/routes';
import { useAppDispatch, useAppThunkDispatch, useDocumentTitle } from '../common/hooks';
import { t } from '../common/i18n';
import { refreshProfile } from '../common/store/securitySlice';
import { setFlashMessage } from '../common/store/uiSlice';
import { formatPrice } from './utils/formatPrice';
import { formatDate } from '../common/utils/Date/Date';
import { downloadOrderDocument, fetchOrderPaymentQrUrl, getOrder } from './services/SubscriptionService';
import { Order } from './types';

const namespace = 'Subscription';

/** Vystavení proformy běží async (Fakturoid) — rychlý polling 10 s, pak pomalý do 5 minut. */
const FAST_POLL_MS = 2_000;
const FAST_POLL_WINDOW_MS = 10_000;
const SLOW_POLL_MS = 10_000;
const POLL_LIMIT_MS = 5 * 60_000;

export const OrderStatus: React.FC = () => {
  useDocumentTitle(t('status.title', namespace));

  const { orderId } = useParams<{ orderId: string }>();
  const dispatch = useAppDispatch();
  const thunkDispatch = useAppThunkDispatch();

  const [order, setOrder] = useState<Order | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isDelayed, setIsDelayed] = useState(false);
  const [downloading, setDownloading] = useState<'proforma' | 'tax-document' | null>(null);
  const [qrUrl, setQrUrl] = useState<string | null>(null);
  const startedAtRef = useRef<number>(Date.now());
  const profileRefreshedRef = useRef(false);

  useEffect(() => {
    if (!orderId) {
      setHasError(true);
      setIsLoading(false);
      return;
    }
    let timeout: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;
    startedAtRef.current = Date.now();

    const load = () => {
      getOrder(orderId)
        .then((data) => {
          if (cancelled) return;
          setOrder(data);
          setIsLoading(false);

          // PDF se stahuje z Fakturoidu až po zápisu metadat — stav objednávky tedy chvíli existuje
          // bez odkazu ke stažení (proforma i zúčtovací faktura) a polling musí pokračovat i po něm.
          const elapsed = Date.now() - startedAtRef.current;
          const isWaiting =
            'created' === data.state ||
            ('proforma_issued' === data.state && !data.hasProformaPdf) ||
            (('paid' === data.state || 'activated' === data.state) && !data.hasTaxDocumentPdf);
          if (isWaiting && elapsed < POLL_LIMIT_MS) {
            if ('created' === data.state && elapsed >= FAST_POLL_WINDOW_MS) setIsDelayed(true);
            timeout = setTimeout(load, elapsed < FAST_POLL_WINDOW_MS ? FAST_POLL_MS : SLOW_POLL_MS);
          }
        })
        .catch(() => {
          if (cancelled) return;
          setHasError(true);
          setIsLoading(false);
        });
    };
    load();

    return () => {
      cancelled = true;
      if (timeout) clearTimeout(timeout);
    };
  }, [orderId]);

  // QR Platba se stahuje autorizovaně jako blob; když ho objednávka nemá (404), sekce se prostě neukáže.
  useEffect(() => {
    if (!orderId || order?.state !== 'proforma_issued') return;
    let objectUrl: string | null = null;
    let cancelled = false;

    fetchOrderPaymentQrUrl(orderId)
      .then((url) => {
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        objectUrl = url;
        setQrUrl(url);
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setQrUrl(null);
    };
  }, [orderId, order?.state]);

  // Objednávka mění sidebar (čekáme na platbu / licence aktivní) — po načtení jednou obnovit profil.
  useEffect(() => {
    if (!order || profileRefreshedRef.current) return;
    if (order.state === 'created') return;
    profileRefreshedRef.current = true;
    thunkDispatch(refreshProfile());
  }, [order, thunkDispatch]);

  const handleDownload = async (document: 'proforma' | 'tax-document') => {
    if (!order) return;
    setDownloading(document);
    const filename =
      document === 'proforma'
        ? `Proforma-${order.proformaNumber ?? 'eVlastnici'}.pdf`
        : `Danovy-doklad-${order.taxDocumentNumber ?? 'eVlastnici'}.pdf`;
    try {
      await downloadOrderDocument(order.id, document, filename);
    } catch {
      dispatch(setFlashMessage({ message: t('status.downloadError', namespace), type: 'error' }));
    } finally {
      setDownloading(null);
    }
  };

  const renderPayment = (data: Order) => (
    <div className="OrderPayment">
      <Text as="h2" size="h3" color="Neutral800" className="OrderPanel__heading">
        {t('status.payment.heading', namespace)}
      </Text>
      <div className="OrderPayment__body">
        <dl className="OrderPayment__list">
          <dt>{t('status.payment.amount', namespace)}</dt>
          <dd className="OrderPayment__amount">{formatPrice(data.priceWithVat)}</dd>
          {data.bankAccount && (
            <>
              <dt>{t('status.payment.account', namespace)}</dt>
              <dd>{data.bankAccount}</dd>
            </>
          )}
          {data.variableSymbol && (
            <>
              <dt>{t('status.payment.variableSymbol', namespace)}</dt>
              <dd>{data.variableSymbol}</dd>
            </>
          )}
          {data.proformaDueOn && (
            <>
              <dt>{t('status.payment.dueOn', namespace)}</dt>
              <dd>{formatDate(data.proformaDueOn)}</dd>
            </>
          )}
        </dl>
        {qrUrl && (
          <figure className="OrderPayment__qr">
            <img src={qrUrl} alt={t('status.payment.qrAlt', namespace)} />
            <figcaption>{t('status.payment.qr', namespace)}</figcaption>
          </figure>
        )}
      </div>
    </div>
  );

  const renderBody = (data: Order) => {
    switch (data.state) {
      case 'created':
        // Fakturoid odpoví obvykle do pár sekund; při výpadku ale může faktura vzniknout až později,
        // proto text nic neslibuje o čase — jen že fakturu pošleme e-mailem.
        return (
          <div className="OrderState">
            {isDelayed ? (
              <div className="OrderState__icon OrderState__icon--info">
                <Icon name="envelope" />
              </div>
            ) : (
              <Spinner size="large" className="mb-2" />
            )}
            <Text as="h1" size="h2" color="Neutral800">
              {t('status.pending.title', namespace)}
            </Text>
            <Text as="p" size="body-large" color="Neutral700">
              {t('status.pending.text', namespace, { email: data.billing.email })}
            </Text>
            <Text as="p" size="body-medium" color="Neutral600">
              {t(isDelayed ? 'status.pending.hintDelayed' : 'status.pending.hint', namespace)}
            </Text>
          </div>
        );

      case 'proforma_issued':
        return (
          <>
            <div className="OrderState">
              <div className="OrderState__icon OrderState__icon--success">
                <Icon name="checkmark-circle" />
              </div>
              <Text as="h1" size="h2" color="Neutral800">
                {t('status.issued.title', namespace)}
              </Text>
              <Text as="p" size="body-large" color="Neutral700">
                {t('status.issued.text', namespace, { number: data.proformaNumber, email: data.billing.email })}
              </Text>
            </div>
            {renderPayment(data)}
            <Text as="p" size="body-medium" color="Neutral700" align="center" className="mt-3">
              {t('status.issued.next', namespace)}
            </Text>
          </>
        );

      case 'paid':
      case 'activated':
        return (
          <div className="OrderState">
            <div className="OrderState__icon OrderState__icon--success">
              <Icon name="shieldCheck" />
            </div>
            <Text as="h1" size="h2" color="Neutral800">
              {t('status.activated.title', namespace)}
            </Text>
            <Text as="p" size="body-large" color="Neutral700">
              {data.product === 'building-subscription' && data.licenseValidTo
                ? t('status.activated.license', namespace, { date: formatDate(data.licenseValidTo) })
                : t('status.activated.poll', namespace)}
            </Text>
            {!data.hasTaxDocumentPdf && (
              <Text as="p" size="body-medium" color="Neutral600">
                {t('status.activated.taxDocumentPending', namespace)}
              </Text>
            )}
          </div>
        );

      case 'cancelled':
        return (
          <div className="OrderState">
            <Text as="h1" size="h2" color="Neutral800">
              {t('status.cancelled.title', namespace)}
            </Text>
            <Text as="p" size="body-large" color="Neutral700">
              {t('status.cancelled.text', namespace)}
            </Text>
          </div>
        );

      case 'failed':
      default:
        return (
          <div className="OrderState">
            <div className="OrderState__icon OrderState__icon--info">
              <Icon name="envelope" />
            </div>
            <Text as="h1" size="h2" color="Neutral800">
              {t('status.failed.title', namespace)}
            </Text>
            <Text as="p" size="body-large" color="Neutral700">
              {t('status.failed.text', namespace, { email: data.billing.email })}
            </Text>
          </div>
        );
    }
  };

  return (
    <>
      <Header title={t('status.title', namespace)} customBacklink={routes.building} />
      <Content isLoading={isLoading} className="Content Subscription">
        {hasError && <FormMessage type="error" message={t('status.notFound', namespace)} />}

        {order && (
          <div className="OrderStatus">
            {renderBody(order)}

            <div className="OrderStatus__actions">
              {order.hasProformaPdf && (
                <Button
                  title={t('status.downloadProforma', namespace)}
                  variant="secondary"
                  iconBefore="download"
                  loading={downloading === 'proforma'}
                  onClick={() => handleDownload('proforma')}
                />
              )}
              {order.hasTaxDocumentPdf && (
                <Button
                  title={t('status.downloadTaxDocument', namespace)}
                  variant="secondary"
                  iconBefore="download"
                  loading={downloading === 'tax-document'}
                  onClick={() => handleDownload('tax-document')}
                />
              )}
              {order.state === 'cancelled' ? (
                <ButtonLink to={routes.subscriptionOrder} title={t('status.cancelled.action', namespace)} />
              ) : (
                <ButtonLink to={routes.root} title={t('status.backToApp', namespace)} variant="text" />
              )}
            </div>
          </div>
        )}
      </Content>
    </>
  );
};

export default OrderStatus;
